import type {BottleVariant} from '../components/rewards/CoffeeCard/bottleGeometry';
import {OffersItem} from '../transport/types';

/**
 * True when a Coffee Card offer has not been earned yet — the member has not
 * bought enough qualifying drinks this cycle to claim the reward.
 *
 * Mirrors the middleware's own `isComplete` formula
 * (services/offers/mappers.ts: `purchased_quantity >= target_quantity`), which
 * the /v2/offers payload computes but does not send to the client.
 *
 * Read from the same two numbers CoffeeCard.tsx draws the bottles from, so the
 * APPLY button can never disagree with the progress the member is looking at.
 * `fully_redeemed` does NOT cover this: it means the card's redemption limit is
 * exhausted, so it is false again on every fresh cycle.
 */
export const isCoffeeCardUnearned = (offer: OffersItem): boolean => {
  if (offer.offerTrigger !== 'Coffee Card') {
    return false;
  }

  const {purchased_quantity, target_quantity} = offer.coffeeCardStats;
  return purchased_quantity < target_quantity;
};

/**
 * A bottle count that `Array(count)` accepts: a non-negative whole number.
 * Negative, NaN and infinite values become 0; fractions round down.
 *
 * DIS-1913: Zen sent `currentPurchases: -1` (the free bottle subtracted twice)
 * and `Array(-1)` crashed the offer screen with "RangeError: Invalid array length".
 */
export const toBottleCount = (value: number): number =>
  Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;

export const EARNED_AWAITING_DEAL_MESSAGE = 'Reward earned. It will be ready to apply soon.';

/**
 * The message for a coffee card that is earned but not yet appliable (DIS-1912),
 * or null for every other offer.
 *
 * Zen marks the card earned straight away. MFL lists the reward deal only after
 * its next sync. On 2026-10-22 one card was still unlisted at least 26
 * minutes after Zen showed it earned; the exact wait was not measured. In
 * that window the middleware sends
 * `disabledReason: "earned_awaiting_deal"`. Every other `disabledReason`
 * value keeps its current display, which is no message.
 */
export const getAwaitingDealMessage = (offer: {disabledReason?: string | null}): string | null =>
  offer.disabledReason === 'earned_awaiting_deal' ? EARNED_AWAITING_DEAL_MESSAGE : null;

const BOTTLE_WORD = /\b(beer|wine)s?\b/i;

const bottleIn = (text: string | null | undefined): BottleVariant | null => {
  const match = text?.match(BOTTLE_WORD);
  return match ? (match[1].toLowerCase() as BottleVariant) : null;
};

/**
 * Which bottle the coffee card draws for this offer (DIS-1777).
 *
 * A set `category` decides: "Beer" draws beer, anything else draws wine.
 * With no category, the first "beer" or "wine" in the headline, tile headline
 * or description decides. With neither, the card draws wine.
 */
export const resolveBottleVariant = (offer: OffersItem): BottleVariant => {
  if (offer.category?.trim()) {
    return bottleIn(offer.category) === 'beer' ? 'beer' : 'wine';
  }

  const labels = [offer.headline, offer.tileHeadline, offer.description].join('\n');
  return bottleIn(labels) ?? 'wine';
};
